/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useNavigate } from 'react-router-dom';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { EntityTile } from '@tailwindComponents/EntityTile';
import {
  productThumbnailSrcOfSku,
  skuOwnThumbnailSrc,
} from '@views/SKUMaster/components/skuHelpers';
import { useAuth } from '@context';
import { UserRoleService } from '@services';
import { useAtomValue } from 'jotai';
import {
  DespatchSplitButton,
  DespatchUnit,
} from '@tailwindComponents/DespatchSplitButton';
import { SkuInfoLink } from '@tailwindComponents/SkuInfoModal';
import { skuProductsPerUnit } from '../../../../utils/sku';
import { InventorySKUListing, RowProps } from '../types';
import {
  displayedBlockedStock,
  displayedLostStock,
  displayedLostProducts,
  displayedSurplusStock,
  displayedSurplusProducts,
} from '../../../../utils/stockDisplay';
import { businessProfileAtom } from '../../../../store/jotai';

export function CollapsibleRow({
  row,
  warehouseId,
  nestedUnderProduct = false,
}: RowProps<InventorySKUListing> & { nestedUnderProduct?: boolean }) {
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();
  const navigate = useNavigate();
  const businessProfile = useAtomValue(businessProfileAtom);

  const allAvailableStock = row.stockByWarehouse.reduce(
    (n, { availableStock }) => n + availableStock,
    0
  );

  const allAllocatedStock = row.stockByWarehouse.reduce(
    (n, { allocatedStock }) => n + allocatedStock,
    0
  );

  const allOutForDeliveryStock = row.stockByWarehouse.reduce(
    (n, { inOutboundTransitStock }) => n + inOutboundTransitStock,
    0
  );

  const allInTransitStock = row.stockByWarehouse.reduce(
    (n, { inInboundTransitStock }) => n + inInboundTransitStock,
    0
  );

  const allTotalStock = row.stockByWarehouse.reduce(
    (n, { totalStock }) => n + totalStock,
    0
  );

  const allAvailableLooseProducts = row.stockByWarehouse.reduce(
    (n, { availableLooseProducts }) => n + (availableLooseProducts ?? 0),
    0
  );

  const allAllocatedLooseProducts = row.stockByWarehouse.reduce(
    (n, { allocatedLooseProducts }) => n + (allocatedLooseProducts ?? 0),
    0
  );

  const allOutboundTransitLooseProducts = row.stockByWarehouse.reduce(
    (n, { inOutboundTransitLooseProducts }) =>
      n + (inOutboundTransitLooseProducts ?? 0),
    0
  );

  const warehouseStock = row.stockByWarehouse.find(
    (warehouse) => warehouse.warehouseId === warehouseId
  );

  const blockedStockValue = displayedBlockedStock(
    row.stockByWarehouse,
    warehouseId
  );
  const lostVal = displayedLostStock(row.stockByWarehouse, warehouseId);
  const lostProductsVal = displayedLostProducts(
    row.stockByWarehouse,
    warehouseId
  );
  const surplusVal = displayedSurplusStock(row.stockByWarehouse, warehouseId);
  const surplusProductsVal = displayedSurplusProducts(
    row.stockByWarehouse,
    warehouseId
  );

  // Stock is held in the SKU's unit (e.g. cases); each holds `amountOfProductInSku` atomic products
  // (e.g. bottles). When a SKU holds more than one product the PRODUCT count is the headline figure
  // and the stock-unit count (e.g. "1 case") is the muted, labelled secondary — otherwise a single
  // number (Item SKUs stay uncluttered).
  const productsPerSku = skuProductsPerUnit(row);
  const showProducts = productsPerSku > 1;
  const unitWord = (row.unitOfMeasure || 'unit').toLowerCase();
  const unitLabel = (units: number) =>
    `${numberToLocale(units)} ${unitWord}${units === 1 ? '' : 's'}`;
  const stockFigure = (
    stockUnits: number,
    primaryClassName: string,
    looseProducts = 0
  ) => {
    if (!showProducts) {
      return (
        <span className={primaryClassName}>{numberToLocale(stockUnits)}</span>
      );
    }
    return (
      <span className='inline-flex flex-col items-center leading-tight'>
        <span className={primaryClassName}>
          {numberToLocale(stockUnits * productsPerSku + looseProducts)}
          <span className='ml-1 text-xs font-normal text-gray-600'>
            products
          </span>
        </span>
        <span className='text-xs font-normal text-gray-600'>
          {unitLabel(stockUnits)}
          {looseProducts > 0 && ` · ${numberToLocale(looseProducts)} loose`}
        </span>
      </span>
    );
  };

  const hasLost = lostVal > 0 || lostProductsVal > 0;
  const hasSurplus = surplusVal > 0 || surplusProductsVal > 0;
  const surplusFigureValue = showProducts
    ? surplusProductsVal || surplusVal
    : surplusVal;
  const discrepancyFigure = () => {
    const primaryClassName = `text-sm ${
      hasLost ? 'text-amber-600 font-medium' : 'text-gray-900'
    }`;
    if (!showProducts) {
      return (
        <span className={primaryClassName}>
          {numberToLocale(lostVal)}
          {hasSurplus && (
            <span className='ml-1 text-xs font-normal text-gray-600'>
              · {numberToLocale(surplusFigureValue)} surplus
            </span>
          )}
        </span>
      );
    }
    return (
      <span className='inline-flex flex-col items-center leading-tight'>
        <span className={primaryClassName}>
          {numberToLocale(lostProductsVal)}
          <span className='ml-1 text-xs font-normal text-gray-600'>
            products
          </span>
        </span>
        <span className='text-xs font-normal text-gray-600'>
          {unitLabel(lostVal)}
          {hasSurplus && ` · ${numberToLocale(surplusFigureValue)} surplus`}
        </span>
      </span>
    );
  };

  const isAll = warehouseId === 'All';
  const inTransitVal = isAll
    ? allInTransitStock
    : warehouseStock?.inInboundTransitStock ?? 0;
  const availableVal = isAll
    ? allAvailableStock
    : warehouseStock?.availableStock ?? 0;
  const allocatedVal = isAll
    ? allAllocatedStock
    : warehouseStock?.allocatedStock ?? 0;
  const outForDeliveryVal = isAll
    ? allOutForDeliveryStock
    : warehouseStock?.inOutboundTransitStock ?? 0;
  const totalVal = isAll ? allTotalStock : warehouseStock?.totalStock ?? 0;

  const availableLooseVal = isAll
    ? allAvailableLooseProducts
    : warehouseStock?.availableLooseProducts ?? 0;
  const allocatedLooseVal = isAll
    ? allAllocatedLooseProducts
    : warehouseStock?.allocatedLooseProducts ?? 0;
  const outForDeliveryLooseVal = isAll
    ? allOutboundTransitLooseProducts
    : warehouseStock?.inOutboundTransitLooseProducts ?? 0;
  const totalLooseVal =
    availableLooseVal + allocatedLooseVal + outForDeliveryLooseVal;

  const canCreateDespatchNote =
    businessProfile?.hasConnectedBankAccount ||
    businessProfile?.areFinancialFeaturesDisabled;

  return (
    <tr className='border-b border-gray-200 hover:bg-gray-50'>
      {/* SKU Name & Details - Combined first column */}
      <td className='px-4 py-3'>
        <div
          className={`flex items-center gap-3 ${
            nestedUnderProduct ? 'ml-6 border-l-2 border-gray-200 pl-4' : ''
          }`}
        >
          <div className='flex shrink-0 items-center gap-1.5'>
            <EntityTile
              name={`${row.productDetails?.name ?? row.name} product`}
              imageSrc={productThumbnailSrcOfSku(row)}
            />
            <EntityTile
              name={`${row.name} SKU`}
              imageSrc={skuOwnThumbnailSrc(row)}
              size='sm'
            />
          </div>
          <div className='min-w-0 flex-1'>
            <SkuInfoLink
              sku={row}
              className='truncate text-sm font-medium text-blue-600'
            >
              {row.name}
            </SkuInfoLink>
          </div>
        </div>
      </td>
      {/* SKU ID */}
      <td className='px-4 py-3'>
        <span className='text-sm text-gray-500'>{row.skuId}</span>
      </td>
      {/* In Transit */}
      <td className='text-center px-4 py-3'>
        {stockFigure(inTransitVal, 'text-sm text-gray-900')}
      </td>
      {/* Available - Green highlight */}
      <td className='text-center px-4 py-3 bg-green-50'>
        {stockFigure(
          availableVal,
          'text-sm font-medium text-green-600',
          availableLooseVal
        )}
      </td>
      {isWarehouseClient(user) && (
        <td className='text-center px-4 py-3'>
          <DespatchSplitButton
            disabled={!canCreateDespatchNote}
            title={
              canCreateDespatchNote
                ? 'Create Despatch Note'
                : 'A bank account must be connected before you can create a despatch note.'
            }
            productCount={availableVal * productsPerSku + availableLooseVal}
            skuUnitLabel={unitLabel(availableVal)}
            showMenu={showProducts}
            onDespatch={(unit: DespatchUnit) =>
              navigate(
                `/despatch-note/create?inventorySkuId=${encodeURIComponent(
                  row.inventorySkuId
                )}&unit=${unit}`
              )
            }
          />
        </td>
      )}
      {/* Allocated */}
      <td className='text-center px-4 py-3'>
        {stockFigure(allocatedVal, 'text-sm text-gray-900', allocatedLooseVal)}
      </td>
      {/* Out for delivery - Blue highlight */}
      <td className='text-center px-4 py-3 bg-blue-50'>
        {stockFigure(
          outForDeliveryVal,
          'text-sm font-medium text-blue-600',
          outForDeliveryLooseVal
        )}
      </td>
      {/* Blocked */}
      <td className='text-center px-4 py-3'>
        {stockFigure(
          blockedStockValue,
          `text-sm ${
            blockedStockValue > 0 ? 'text-red-600 font-medium' : 'text-gray-900'
          }`
        )}
      </td>
      {/* Total - Blue highlight */}
      <td className='text-center px-4 py-3 bg-blue-50'>
        {stockFigure(
          totalVal,
          'text-sm font-medium text-gray-900',
          totalLooseVal
        )}
      </td>
      {/* Discrepancy - Amber highlight */}
      <td className='text-center px-4 py-3 bg-amber-50'>
        {discrepancyFigure()}
      </td>
    </tr>
  );
}
